import { c as _c } from "react/compiler-runtime";
import { MediaImageList, NavArrowLeft } from '@byway/icons';
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useState } from 'react';
import Slider from 'react-slick';
import '~/assets/components/slider.css';
import { Button } from 'ui';
import { Image } from '~/components/Image/Image';
import ImageGrid from '~/components/Images/ImageGrid';
import useJourneyData from '~/hooks/useJourneyData';
import { logError } from '~/utils';

// Desktop hero is a fixed 4-col x 2-row grid (8 cells). The hero always takes
// the left 2x2 block; these layouts tile the remaining cells so 1-5 images all
// render gap-free. Keyed by teaser count (header_images minus the hero).
//
// Teasers are lazy and the grid is `display:none` below `md`, so their mobile
// fallback is `1px` (never fetch a desktop-only tile - the mobile carousel
// renders these same images). The hero, by contrast, is eager, so it keeps a
// `100vw` mobile fallback to reuse the carousel hero's candidate.
const TEASER_LAYOUTS = {
  1: [{
    cls: 'col-span-2 row-span-2',
    sizes: '(min-width: 768px) 50vw, 1px'
  }],
  2: [{
    cls: 'col-span-2 row-span-1',
    sizes: '(min-width: 768px) 50vw, 1px'
  }, {
    cls: 'col-span-2 row-span-1',
    sizes: '(min-width: 768px) 50vw, 1px'
  }],
  3: [{
    cls: 'col-span-2 row-span-1',
    sizes: '(min-width: 768px) 50vw, 1px'
  }, {
    cls: 'col-span-1 row-span-1',
    sizes: '(min-width: 768px) 25vw, 1px'
  }, {
    cls: 'col-span-1 row-span-1',
    sizes: '(min-width: 768px) 25vw, 1px'
  }],
  4: [{
    cls: 'col-span-1 row-span-1',
    sizes: '(min-width: 768px) 25vw, 1px'
  }, {
    cls: 'col-span-1 row-span-1',
    sizes: '(min-width: 768px) 25vw, 1px'
  }, {
    cls: 'col-span-1 row-span-1',
    sizes: '(min-width: 768px) 25vw, 1px'
  }, {
    cls: 'col-span-1 row-span-1',
    sizes: '(min-width: 768px) 25vw, 1px'
  }]
};
const GALLERY_TILE_SIZES = [{
  width: 600,
  height: 400
}, {
  width: 320,
  height: 480
}, {
  width: 540,
  height: 300
}, {
  width: 380,
  height: 560
}, {
  width: 460,
  height: 360
}];
function useImagePreloader(images) {
  const $ = _c(3);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  let t0;
  let t1;
  if ($[0] !== images) {
    t0 = () => {
      if (!images?.length) {
        setImagesLoaded(false);
        return;
      }
      const loadImage = _temp;
      let mounted = true;
      Promise.all(images.map(loadImage)).then(() => {
        if (mounted) {
          setImagesLoaded(true);
        }
      }).catch(err => {
        logError(`Caught Error: Failed to load image - ${err}`);
        if (mounted) {
          setImagesLoaded(false);
        }
      });
      return () => {
        mounted = false;
      };
    };
    t1 = [images];
    $[0] = images;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  return imagesLoaded;
}
function _temp(image) {
  return new Promise((resolve, reject) => {
    if (!image?.src) {
      reject(new Error("Invalid image source"));
      return;
    }
    const loadingImage = new window.Image();
    loadingImage.src = image.src;
    loadingImage.onload = resolve;
    loadingImage.onerror = () => {
      reject(new Error(`Failed to load image: ${image.src}`));
    };
  });
}
export default function JourneyHeader() {
  const $ = _c(74);
  const {
    header_images
  } = useJourneyData();
  const tAria = useTranslations("aria");
  const tCommon = useTranslations("common");
  const [isOverlayActive, setIsOverlayActive] = useState(false);
  const [slideIndex, setSlideIndex] = useState(0);
  let t0;
  if ($[0] !== header_images) {
    t0 = header_images ?? [];
    $[0] = header_images;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = t0.map(_temp2);
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const processedImages = t1;
  const imagesLoaded = useImagePreloader(processedImages);
  if (!header_images?.length) {
    return null;
  }
  let t2;
  if ($[4] !== header_images) {
    t2 = header_images.slice(0, 5);
    $[4] = header_images;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let heroImage;
  let teaserImages;
  if ($[6] !== t2) {
    [heroImage, ...teaserImages] = t2;
    $[6] = t2;
    $[7] = heroImage;
    $[8] = teaserImages;
  } else {
    heroImage = $[7];
    teaserImages = $[8];
  }
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[9] !== heroImage.alt || $[10] !== heroImage.caption || $[11] !== heroImage.src || $[12] !== teaserImages) {
    const teaserLayout = TEASER_LAYOUTS[teaserImages.length] ?? [];
    const heroSpan = teaserImages.length === 0 ? "col-span-4 row-span-2" : "col-span-2 row-span-2";
    const heroSizes = teaserImages.length === 0 ? "100vw" : "(min-width: 768px) 50vw, 100vw";
    t7 = "relative";
    t3 = "journey-header-grid";
    t4 = "hidden h-[40vh] tall:h-auto tall:aspect-[8/3] tall:max-h-full grid-cols-4 grid-rows-2 gap-2 mt-8 overflow-hidden rounded-lg md:grid";
    let t8;
    if ($[18] !== heroSpan) {
      t8 = clsx("relative", heroSpan);
      $[18] = heroSpan;
      $[19] = t8;
    } else {
      t8 = $[19];
    }
    const t9 = heroImage.alt ?? "";
    let t10;
    if ($[20] !== heroImage.caption || $[21] !== heroImage.src || $[22] !== heroSizes || $[23] !== t9) {
      t10 = <Image src={heroImage.src} alt={t9} fill={true} sizes={heroSizes} priority={true} label={heroImage.caption} />;
      $[20] = heroImage.caption;
      $[21] = heroImage.src;
      $[22] = heroSizes;
      $[23] = t9;
      $[24] = t10;
    } else {
      t10 = $[24];
    }
    if ($[25] !== t10 || $[26] !== t8) {
      t5 = <div className={t8}>{t10}</div>;
      $[25] = t10;
      $[26] = t8;
      $[27] = t5;
    } else {
      t5 = $[27];
    }
    t6 = teaserImages.map((image_0, index_0) => <div key={`header-grid-${image_0.src}`} className={clsx("relative", teaserLayout[index_0]?.cls)}><Image src={image_0.src} alt={image_0.alt ?? ""} fill={true} sizes={teaserLayout[index_0]?.sizes ?? "(min-width: 768px) 25vw, 1px"} label={image_0.caption} /></div>);
    $[9] = heroImage.alt;
    $[10] = heroImage.caption;
    $[11] = heroImage.src;
    $[12] = teaserImages;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
  } else {
    t3 = $[13];
    t4 = $[14];
    t5 = $[15];
    t6 = $[16];
    t7 = $[17];
  }
  let t8;
  if ($[28] !== t3 || $[29] !== t4 || $[30] !== t5 || $[31] !== t6) {
    t8 = <div data-test-id={t3} className={t4}>{t5}{t6}</div>;
    $[28] = t3;
    $[29] = t4;
    $[30] = t5;
    $[31] = t6;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  let t9;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = i => setSlideIndex(i);
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  const t10 = header_images.length > 1;
  const t11 = header_images.length > 1;
  let t12;
  if ($[34] !== slideIndex) {
    t12 = i_0 => <span className={clsx("block w-2 h-2 rounded-full", i_0 === slideIndex ? "bg-primary-500" : "bg-neutral-200")} />;
    $[34] = slideIndex;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== header_images) {
    t13 = header_images.map(_temp4);
    $[36] = header_images;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  let t14;
  if ($[38] !== t10 || $[39] !== t11 || $[40] !== t12 || $[41] !== t13) {
    t14 = <div className="md:hidden"><Slider afterChange={t9} dots={t10} infinite={t11} className="w-full" arrows={false} dotsClass="w-full absolute bottom-6 flex justify-center space-x-1" appendDots={_temp3} customPaging={t12}>{t13}</Slider></div>;
    $[38] = t10;
    $[39] = t11;
    $[40] = t12;
    $[41] = t13;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  let t15;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => {
      setIsOverlayActive(true);
    };
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  let t16;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <span className="md:mr-1"><MediaImageList decorative={true} isInline={true} size="large" /></span>;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== tAria) {
    t17 = tAria("image_gallery_show_all");
    $[45] = tAria;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  let t18;
  if ($[47] !== t17) {
    t18 = <div className="absolute right-3 bottom-4 md:bottom-2"><Button type="glassy" size="small" onClick={t15} data-ua-click="show-all-photos-button">{t16}<span className="hidden md:inline">{t17}</span></Button></div>;
    $[47] = t17;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== t14 || $[50] !== t18 || $[51] !== t7 || $[52] !== t8) {
    t19 = <div className={t7}>{t8}{t14}{t18}</div>;
    $[49] = t14;
    $[50] = t18;
    $[51] = t7;
    $[52] = t8;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  let t20;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = () => setIsOverlayActive(false);
    $[54] = t20;
  } else {
    t20 = $[54];
  }
  let t21;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = () => setIsOverlayActive(false);
    $[55] = t21;
  } else {
    t21 = $[55];
  }
  let t22;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <NavArrowLeft decorative={true} isInline={true} size="large" />;
    $[56] = t22;
  } else {
    t22 = $[56];
  }
  let t23;
  if ($[57] !== tAria) {
    t23 = tAria("back");
    $[57] = tAria;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  let t24;
  if ($[59] !== t23) {
    t24 = <Button onClick={t21} type="glassy" size="small">{t22}{t23}</Button>;
    $[59] = t23;
    $[60] = t24;
  } else {
    t24 = $[60];
  }
  let t25;
  if ($[61] !== imagesLoaded || $[62] !== processedImages || $[63] !== tCommon) {
    t25 = <div className="mt-4">{imagesLoaded ? <ImageGrid images={processedImages} margin={16} rowHeight={600} /> : <div className="flex items-center justify-center h-64"><div className="text-gray-500">{tCommon("loading")}</div></div>}</div>;
    $[61] = imagesLoaded;
    $[62] = processedImages;
    $[63] = tCommon;
    $[64] = t25;
  } else {
    t25 = $[64];
  }
  let t26;
  if ($[65] !== t24 || $[66] !== t25) {
    t26 = <Dialog as="div" className="fixed inset-0 z-[100000] overflow-y-auto" onClose={t20}><div className="min-h-screen px-4"><TransitionChild as="div" enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0"><DialogPanel className="fixed inset-0 flex flex-col bg-white"><div className="flex-1 px-4 overflow-y-auto md:px-8"><div className="container relative mx-auto my-12">{t24}{t25}</div></div></DialogPanel></TransitionChild></div></Dialog>;
    $[65] = t24;
    $[66] = t25;
    $[67] = t26;
  } else {
    t26 = $[67];
  }
  let t27;
  if ($[68] !== isOverlayActive || $[69] !== t26) {
    t27 = <Transition appear={true} show={isOverlayActive} as="div">{t26}</Transition>;
    $[68] = isOverlayActive;
    $[69] = t26;
    $[70] = t27;
  } else {
    t27 = $[70];
  }
  let t28;
  if ($[71] !== t19 || $[72] !== t27) {
    t28 = <div>{t19}{t27}</div>;
    $[71] = t19;
    $[72] = t27;
    $[73] = t28;
  } else {
    t28 = $[73];
  }
  return t28;
}
function _temp4(image_1, index_1) {
  return <div key={`${image_1.src}-${index_1}-carousel`} className="relative w-full aspect-[4/3]"><Image src={image_1.src} alt={image_1.alt ?? ""} fill={true} sizes="(max-width: 767px) 100vw, 1px" priority={index_1 === 0} /></div>;
}
function _temp3(dots) {
  return <ul>{dots}</ul>;
}
function _temp2(image, index) {
  return {
    src: image?.src || "",
    ...GALLERY_TILE_SIZES[index % GALLERY_TILE_SIZES.length],
    credit: image?.credit || ""
  };
}